import React, { useState, useEffect, useMemo } from "react";
import {
  Card, CardBody, Col, Container, Row,
  Button, Table, FormGroup, Label,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { getCampaignReport, exportCampaignReport } from "../../components/Api/GmsCampaignReport";
import { getCampaigns } from "../../components/Api/GmsCampaign";
import { getSources } from "../../components/Api/GmsSource";
import { getBranches } from "../../components/Api/GmsBranch";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { ClipLoader } from "react-spinners";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { hasPagePermissionWithAdmin } from "../../components/Permission/PermissionService";
import Swal from "sweetalert2";
import {
  TrendingUp, TrendingDown, DollarSign, Users,
  ShoppingBag, BarChart2, RefreshCw, Calendar, Download,
} from "lucide-react";

// ─── Session helper ───────────────────────────────────────────────────────────
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const normalizeId = (v) => {
    if (v === null || v === undefined || v === "" || v === "null") return null;
    const n = typeof v === "string" ? parseInt(v, 10) : v;
    return isNaN(n) || n <= 0 ? null : n;
  };
  return {
    businessId: normalizeId(authUser.companyId || user.user_Company_Id),
    branchId: normalizeId(authUser.branchId || user.user_Branch_Id),
  };
};

// ─── Format helpers ───────────────────────────────────────────────────────────
const fmt = (v, decimals = 2) =>
  (parseFloat(v) || 0).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

// ─── Select styles ────────────────────────────────────────────────────────────
const selectStyles = {
  control: (p, s) => ({
    ...p,
    minHeight: "38px",
    borderColor: s.isFocused ? "#556ee6" : "#ced4da",
    boxShadow: s.isFocused ? "0 0 0 0.2rem rgba(85,110,230,0.25)" : "none",
  }),
  menu: (p) => ({ ...p, zIndex: 9999, maxHeight: 200 }),
  menuList: (p) => ({ ...p, maxHeight: 180, overflowY: "auto" }),
};

// ─── ROI badge ────────────────────────────────────────────────────────────────
const roiBadge = (roi) => {
  if (roi === null || roi === undefined)
    return (
      <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#6c757d" }}>
        N/A
      </span>
    );
  const v = parseFloat(roi);
  const color = v >= 100 ? "#27ae60" : v >= 0 ? "#3498db" : "#e74c3c";
  return (
    <span
      style={{
        fontSize: "0.8rem",
        fontWeight: 700,
        color: "#fff",
        backgroundColor: color,
        borderRadius: "12px",
        padding: "2px 8px",
        display: "inline-block",
      }}
    >
      {fmt(v, 1)}%
    </span>
  );
};

// ─── P&L coloured text ────────────────────────────────────────────────────────
const plText = (pl) => {
  const v = parseFloat(pl) || 0;
  return (
    <span style={{ fontWeight: 700, color: v >= 0 ? "#27ae60" : "#e74c3c" }}>
      {v >= 0 ? "+" : ""}
      {fmt(v)}
    </span>
  );
};

// ─── KPI tile ─────────────────────────────────────────────────────────────────
// Fixed height, consistent layout: label on top, big value, optional sub-line
const KpiTile = ({ icon: Icon, label, value, sub, iconColor, iconBg }) => (
  <Card
    style={{
      border: "none",
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
      borderRadius: "12px",
      height: "100%",
    }}
  >
    <CardBody
      style={{
        padding: "16px",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        minHeight: "90px",
      }}
    >
      {/* Icon box */}
      <div
        style={{
          flexShrink: 0,
          width: 48,
          height: 48,
          borderRadius: "12px",
          backgroundColor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={22} color={iconColor} />
      </div>

      {/* Text */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <p
          className="mb-0 text-muted"
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            lineHeight: 1.2,
          }}
        >
          {label}
        </p>
        <p
          className="mb-0"
          style={{
            fontSize: "1.1rem",
            fontWeight: 800,
            color: "#2c3e50",
            lineHeight: 1.3,
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </p>
        {sub && (
          <p
            className="mb-0 text-muted"
            style={{ fontSize: "0.72rem", lineHeight: 1.2, marginTop: 2 }}
          >
            {sub}
          </p>
        )}
      </div>
    </CardBody>
  </Card>
);

// ─── Main component ───────────────────────────────────────────────────────────
export default function CampaignReportPage() {
  const session = useMemo(() => getUserSession(), []);
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const hasPermission = hasPagePermissionWithAdmin(permissions, "/GmsCampaignReport");
  const currencyCode = localStorage.getItem("currencyCode") || "AED";

  // Permission guard
  useEffect(() => {
    if (!hasPermission) window.location.href = "/Dashboard";
  }, [hasPermission]);

  // ── Dropdown data ──────────────────────────────────────────────────────────
  const [sourceOptions, setSourceOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [businessOptions, setBusinessOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  // ── Results state ──────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // ── Load businesses on mount, auto-bind from session ─────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await getBusinesses({ business_Active: true });
        const data = Array.isArray(res)
          ? res
          : res?.Businesses || res?.businesses || res?.data || [];
        const opts = data.map((b) => ({
          value: b.Business_Id || b.business_Id,
          label: b.Business_NameEn || b.business_NameEn,
        }));
        setBusinessOptions(opts);
        // Auto-bind from session
        if (session.businessId) {
          const match = opts.find((o) => o.value === Number(session.businessId));
          if (match) setSelectedBusiness(match);
        }
      } catch { /* silent */ }
    };
    load();
  }, [session.businessId]);

  // ── Load branches whenever selected business changes ──────────────────────
  useEffect(() => {
    const load = async () => {
      setBranchOptions([]);
      setSelectedBranch(null);
      if (!selectedBusiness) return;
      try {
        const res = await getBranches({
          Branch_Buisness_Id: selectedBusiness.value,
          branch_Active: true,
        });
        const data = Array.isArray(res)
          ? res
          : res?.Branches || res?.branches || res?.data || [];
        const opts = data.map((b) => ({
          value: b.Branch_Id || b.branch_Id,
          label: b.Branch_NameEn || b.branch_NameEn,
        }));
        setBranchOptions(opts);
        // Auto-bind branch from session if it belongs to this business
        if (session.branchId) {
          const match = opts.find((o) => o.value === Number(session.branchId));
          if (match) setSelectedBranch(match);
        }
      } catch { /* silent */ }
    };
    load();
  }, [selectedBusiness, session.branchId]);

  // ── Load sources ───────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await getSources({ Page: 1, PageSize: 1000 });
        const data = Array.isArray(res) ? res : res?.Sources || res?.sources || [];
        setSourceOptions(
          data.map((s) => ({
            value: s.Source_Id || s.source_Id,
            label: s.SourceName || s.sourceName,
          }))
        );
      } catch { /* silent */ }
    };
    load();
  }, []);

  // ── Load campaigns (re-run when source changes) ────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const filter = { Page: 1, PageSize: 1000 };
        if (selectedSource) filter.SourceId = selectedSource.value;
        if (session.businessId) filter.BusinessId = session.businessId;
        const res = await getCampaigns(filter);
        const data = res?.Campaigns || res?.campaigns || [];
        const opts = data.map((c) => ({
          value: c.campaignId || c.CampaignId,
          label: c.campaignName || c.CampaignName,
        }));
        setCampaignOptions(opts);
        // Remove selections no longer in options
        if (selectedCampaigns.length > 0) {
          const ids = data.map((c) => c.campaignId || c.CampaignId);
          setSelectedCampaigns((prev) => prev.filter((p) => ids.includes(p.value)));
        }
      } catch { /* silent */ }
    };
    load();
  }, [selectedSource, session.businessId]);

  // ── Search handler ─────────────────────────────────────────────────────────
  const handleSearch = async () => {
    // Mandatory: business and branch must be selected
    if (!selectedBusiness || !selectedBranch) {
      Swal.fire("Validation", "Please select both Company and Branch before searching.", "warning");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const filter = {
        SourceId: selectedSource?.value || null,
        CampaignId: null,
        CampaignIds: selectedCampaigns.length > 0
          ? selectedCampaigns.map((c) => c.value)
          : null,
        FromDate: dateRange[0] ? dateRange[0].toISOString() : null,
        ToDate: dateRange[1] ? dateRange[1].toISOString() : null,
        BusinessId: selectedBusiness.value,
        BranchId: selectedBranch.value,
      };
      const res = await getCampaignReport(filter);
      const rows = Array.isArray(res) ? res : res?.data || res?.Data || [];
      setReportData(rows);
    } catch (err) {
      Swal.fire(
        "Error",
        "Failed to load campaign report: " + (err.response?.data?.message || err.message),
        "error"
      );
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedSource(null);
    setSelectedCampaigns([]);
    setDateRange([null, null]);
    // Re-bind business/branch from session on reset
    if (session.businessId) {
      const biz = businessOptions.find((o) => o.value === Number(session.businessId));
      if (biz) setSelectedBusiness(biz);
    }
    setReportData([]);
    setHasSearched(false);
  };

  const handleExport = async () => {
    if (!selectedBusiness || !selectedBranch) return;
    setExporting(true);
    try {
      const filter = {
        SourceId: selectedSource?.value || null,
        CampaignId: null,
        CampaignIds: selectedCampaigns.length > 0 ? selectedCampaigns.map((c) => c.value) : null,
        FromDate: dateRange[0] ? dateRange[0].toISOString() : null,
        ToDate: dateRange[1] ? dateRange[1].toISOString() : null,
        BusinessId: selectedBusiness.value,
        BranchId: selectedBranch.value,
      };
      await exportCampaignReport(filter);
    } catch (err) {
      Swal.fire("Error", "Failed to export report: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setExporting(false);
    }
  };

  // ── Grand-total row ────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    if (!reportData.length) return null;
    return reportData.reduce(
      (acc, r) => ({
        spend: acc.spend + (parseFloat(r.campaignSpend) || 0),
        grossBilled: acc.grossBilled + (parseFloat(r.grossBilled) || 0),
        netBilled: acc.netBilled + (parseFloat(r.netBilled) || 0),
        invoices: acc.invoices + (parseInt(r.totalInvoices) || 0),
        returns: acc.returns + (parseInt(r.totalReturns) || 0),
        customers: acc.customers + (parseInt(r.uniqueCustomers) || 0),
        discount: acc.discount + (parseFloat(r.totalDiscount) || 0),
      }),
      { spend: 0, grossBilled: 0, netBilled: 0, invoices: 0, returns: 0, customers: 0, discount: 0 }
    );
  }, [reportData]);

  const overallROI =
    totals && totals.spend > 0
      ? ((totals.netBilled - totals.spend) / totals.spend) * 100
      : null;

  if (!hasPermission) return null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Campaign Report" breadcrumbItem="Reports" />

          {/* ── Filter Card ─────────────────────────────────────────────── */}
          <Card
            className="shadow-sm border-0 mb-3"
            style={{ borderRadius: "12px" }}
          >
            <CardBody>
              <h6 className="fw-bold mb-3 text-primary">
                <BarChart2 size={16} className="me-1" />
                Report Filters
              </h6>
              <Row className="g-2 align-items-end">

                {/* Company — mandatory */}
                <Col md={2}>
                  <FormGroup className="mb-0">
                    <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                      Company <span className="text-danger">*</span>
                    </Label>
                    <Select
                      options={businessOptions}
                      value={selectedBusiness}
                      onChange={(opt) => {
                        setSelectedBusiness(opt);
                        setSelectedBranch(null);
                      }}
                      styles={selectStyles}
                      placeholder="Select Company"
                      isClearable={false}
                    />
                  </FormGroup>
                </Col>

                {/* Branch — mandatory */}
                <Col md={2}>
                  <FormGroup className="mb-0">
                    <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                      Branch <span className="text-danger">*</span>
                    </Label>
                    <Select
                      options={branchOptions}
                      value={selectedBranch}
                      onChange={setSelectedBranch}
                      styles={selectStyles}
                      placeholder={selectedBusiness ? "Select Branch" : "Select Company first"}
                      isClearable={false}
                      isDisabled={!selectedBusiness}
                    />
                  </FormGroup>
                </Col>

                {/* Source */}
                <Col md={2} style={{ paddingLeft: 0 }}>
                  <FormGroup className="mb-0">
                    <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                      Source
                    </Label>
                    <Select
                      options={sourceOptions}
                      value={selectedSource}
                      onChange={(opt) => {
                        setSelectedSource(opt);
                        setSelectedCampaigns([]);
                      }}
                      styles={selectStyles}
                      placeholder="All Sources"
                      isClearable
                    />
                  </FormGroup>
                </Col>

                {/* Campaign(s) */}
                <Col md={2}>
                  <FormGroup className="mb-0">
                    <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                      Campaign(s)
                    </Label>
                    <Select
                      options={campaignOptions}
                      value={selectedCampaigns}
                      onChange={(opts) => setSelectedCampaigns(opts || [])}
                      styles={selectStyles}
                      placeholder="All Campaigns"
                      isMulti
                      isClearable
                    />
                  </FormGroup>
                </Col>

                {/* Date range */}
                <Col md={3}>
                  <FormGroup className="mb-0">
                    <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                      Invoice Date Range
                    </Label>
                    <Flatpickr
                      className="form-control"
                      placeholder="Select date range"
                      options={{ mode: "range", dateFormat: "d-m-Y" }}
                      value={dateRange.filter(Boolean)}
                      onChange={(dates) =>
                        setDateRange(
                          dates.length === 2 ? dates : [dates[0] || null, null]
                        )
                      }
                    />
                  </FormGroup>
                </Col>

                {/* Buttons */}
                <Col md={1}>
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={handleSearch}
                      disabled={loading}
                      className="w-100"
                      title="Search"
                    >
                      {loading ? <ClipLoader size={14} color="#fff" /> : "Search"}
                    </Button>
                    <Button
                      color="light"
                      onClick={handleReset}
                      disabled={loading}
                      title="Reset filters"
                    >
                      <RefreshCw size={14} />
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* ── KPI Tiles ───────────────────────────────────────────────── */}
          {hasSearched && !loading && totals && (
            <Row className="mb-3 g-3">
              <Col xs={6} sm={4} md={2}>
                <KpiTile
                  icon={DollarSign}
                  label="Total Spend"
                  value={`${currencyCode} ${fmt(totals.spend)}`}
                  iconColor="#e74c3c"
                  iconBg="#fdecea"
                />
              </Col>
              <Col xs={6} sm={4} md={2}>
                <KpiTile
                  icon={ShoppingBag}
                  label="Net Billed"
                  value={`${currencyCode} ${fmt(totals.netBilled)}`}
                  sub={`Gross: ${currencyCode} ${fmt(totals.grossBilled)}`}
                  iconColor="#2ecc71"
                  iconBg="#eafaf1"
                />
              </Col>
              <Col xs={6} sm={4} md={2}>
                <KpiTile
                  icon={overallROI !== null && overallROI >= 0 ? TrendingUp : TrendingDown}
                  label="Overall ROI"
                  value={overallROI !== null ? `${fmt(overallROI, 1)}%` : "N/A"}
                  sub={`P&L: ${currencyCode} ${fmt(totals.netBilled - totals.spend)}`}
                  iconColor={overallROI !== null && overallROI >= 0 ? "#27ae60" : "#e74c3c"}
                  iconBg={overallROI !== null && overallROI >= 0 ? "#eafaf1" : "#fdecea"}
                />
              </Col>
              <Col xs={6} sm={4} md={2}>
                <KpiTile
                  icon={Users}
                  label="Unique Customers"
                  value={totals.customers.toLocaleString()}
                  sub={`${totals.invoices} invoice${totals.invoices !== 1 ? "s" : ""}`}
                  iconColor="#3498db"
                  iconBg="#ebf5fb"
                />
              </Col>
              <Col xs={6} sm={4} md={2}>
                <KpiTile
                  icon={Calendar}
                  label="Total Discount"
                  value={`${currencyCode} ${fmt(totals.discount)}`}
                  iconColor="#9b59b6"
                  iconBg="#f5eef8"
                />
              </Col>
              <Col xs={6} sm={4} md={2}>
                <KpiTile
                  icon={RefreshCw}
                  label="Total Returns"
                  value={totals.returns.toLocaleString()}
                  sub={
                    totals.invoices > 0
                      ? `${fmt((totals.returns / totals.invoices) * 100, 1)}% rate`
                      : "0.0% rate"
                  }
                  iconColor="#e67e22"
                  iconBg="#fef9e7"
                />
              </Col>
            </Row>
          )}

          {/* ── Detail Table ─────────────────────────────────────────────── */}
          <Card className="shadow-sm border-0" style={{ borderRadius: "12px" }}>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold text-primary mb-0">
                  <BarChart2 size={16} className="me-1" />
                  Campaign Performance Details
                  {reportData.length > 0 && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: "#fff",
                        backgroundColor: "#556ee6",
                        borderRadius: "12px",
                        padding: "2px 10px",
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    >
                      {reportData.length} campaign{reportData.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </h6>
                {reportData.length > 0 && (
                  <Button
                    color="success"
                    size="sm"
                    onClick={handleExport}
                    disabled={exporting}
                    style={{ minWidth: 140 }}
                  >
                    {exporting ? (
                      <><ClipLoader size={13} color="#fff" className="me-1" /> Exporting...</>
                    ) : (
                      <><Download size={14} className="me-1" /> Export to Excel</>
                    )}
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <ClipLoader color="#556ee6" size={40} />
                  <p className="mt-2 text-muted">Loading report...</p>
                </div>
              ) : !hasSearched ? (
                <div className="text-center py-5 text-muted">
                  <BarChart2 size={48} style={{ opacity: 0.2 }} />
                  <p className="mt-2">
                    Select a <strong>Branch</strong> and click{" "}
                    <strong>Search</strong> to generate the report.
                  </p>
                </div>
              ) : reportData.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>No data found for the selected filters.</p>
                </div>
              ) : (
                /* Horizontal scroll wrapper */
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                  <Table
                    bordered
                    hover
                    className="align-middle mb-0"
                    style={{ fontSize: "0.82rem", minWidth: 1400 }}
                  >
                    <thead style={{ backgroundColor: "#f0f3ff" }}>
                      <tr>
                        <th style={{ minWidth: 36 }}>#</th>
                        <th style={{ minWidth: 160 }}>Campaign</th>
                        <th style={{ minWidth: 120 }}>Source</th>
                        <th className="text-center" style={{ minWidth: 80 }}>Duration</th>
                        <th className="text-center" style={{ minWidth: 140 }}>Valid Range</th>
                        <th className="text-end" style={{ minWidth: 110 }}>
                          Spend ({currencyCode})
                        </th>
                        <th className="text-end" style={{ minWidth: 100 }}>Gross Billed</th>
                        <th className="text-end" style={{ minWidth: 90 }}>Discount</th>
                        <th className="text-end" style={{ minWidth: 100 }}>Net Billed</th>
                        <th className="text-end" style={{ minWidth: 100 }}>P &amp; L</th>
                        <th className="text-center" style={{ minWidth: 80 }}>ROI</th>
                        <th className="text-center" style={{ minWidth: 70 }}>Invoices</th>
                        <th className="text-center" style={{ minWidth: 70 }}>Returns</th>
                        <th className="text-center" style={{ minWidth: 80 }}>Customers</th>
                        <th className="text-end" style={{ minWidth: 90 }}>Avg Order</th>
                        <th className="text-end" style={{ minWidth: 80 }}>CAC</th>
                        <th className="text-center" style={{ minWidth: 90 }}>Return Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.map((row, idx) => {
                        return (
                          <tr key={row.campaignId || idx}>
                            <td>{idx + 1}</td>
                            <td>
                              <div className="fw-bold text-primary" style={{ lineHeight: 1.3 }}>
                                {row.campaignName}
                              </div>
                              {row.campaignDetails && (
                                <div
                                  className="text-muted"
                                  style={{
                                    fontSize: "0.72rem",
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={row.campaignDetails}
                                >
                                  {row.campaignDetails}
                                </div>
                              )}
                            </td>
                            <td>
                              <span className="fw-semibold">{row.sourceName}</span>
                              {row.isInfluencer && (
                                <span
                                  style={{
                                    marginLeft: 4,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    color: "#fff",
                                    backgroundColor: "#f39c12",
                                    borderRadius: "10px",
                                    padding: "1px 6px",
                                  }}
                                >
                                  Influencer
                                </span>
                              )}
                            </td>
                            <td className="text-center">
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  background: "#f0f3ff",
                                  borderRadius: 8,
                                  padding: "2px 8px",
                                  fontWeight: 600,
                                  color: "#556ee6",
                                }}
                              >
                                {row.campaignDurationDays} days
                              </span>
                            </td>
                            <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                              <small>
                                {new Date(row.validFrom).toLocaleDateString()} &ndash;{" "}
                                {new Date(row.validTo).toLocaleDateString()}
                              </small>
                            </td>
                            <td className="text-end fw-semibold text-danger">
                              {fmt(row.campaignSpend)}
                            </td>
                            <td className="text-end text-muted">{fmt(row.grossBilled)}</td>
                            <td className="text-end text-muted">{fmt(row.totalDiscount)}</td>
                            <td className="text-end fw-bold text-success">
                              {fmt(row.netBilled)}
                            </td>
                            <td className="text-end">{plText(row.profitLoss)}</td>
                            <td className="text-center">{roiBadge(row.roiPercent)}</td>
                            <td className="text-center">{row.totalInvoices ?? 0}</td>
                            <td className="text-center">
                              {(row.totalReturns ?? 0) > 0 ? (
                                <span className="fw-semibold text-danger">{row.totalReturns}</span>
                              ) : (
                                <span className="text-muted">0</span>
                              )}
                            </td>
                            <td className="text-center">{row.uniqueCustomers ?? 0}</td>
                            <td className="text-end">{fmt(row.avgOrderValue)}</td>
                            <td className="text-end">
                              {row.customerAcquisitionCost !== null &&
                              row.customerAcquisitionCost !== undefined
                                ? fmt(row.customerAcquisitionCost)
                                : <span className="text-muted">N/A</span>}
                            </td>
                            <td className="text-center">
                              {(row.returnRatePercent ?? 0) > 0 ? (
                                <span className="text-danger fw-semibold">
                                  {fmt(row.returnRatePercent, 1)}%
                                </span>
                              ) : (
                                <span className="text-muted">0%</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>

                    {/* Grand total footer */}
                    {totals && (
                      <tfoot>
                        <tr
                          style={{
                            backgroundColor: "#f0f3ff",
                            fontWeight: 700,
                            borderTop: "2px solid #c5cef9",
                          }}
                        >
                          <td colSpan={5} className="text-end">
                            Grand Total
                          </td>
                          <td className="text-end text-danger">{fmt(totals.spend)}</td>
                          <td className="text-end">{fmt(totals.grossBilled)}</td>
                          <td className="text-end">{fmt(totals.discount)}</td>
                          <td className="text-end text-success">{fmt(totals.netBilled)}</td>
                          <td className="text-end">{plText(totals.netBilled - totals.spend)}</td>
                          <td className="text-center">{roiBadge(overallROI)}</td>
                          <td className="text-center">{totals.invoices}</td>
                          <td className="text-center text-danger">{totals.returns}</td>
                          <td className="text-center">{totals.customers}</td>
                          <td colSpan={3} />
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}
